import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/lib/useSettings";
import { ArrowRight, Briefcase, Home as HomeIcon, Shield, IdCard, FileCheck, Globe, MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { getServices } from "@/api/services";
import { getPublishedAnnouncements } from "@/api/announcements";
import { addContactMessage } from "@/api/contact";
import { SEO } from "@/components/site/SEO";

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const iconMap: Record<string, any> = { briefcase: Briefcase, home: HomeIcon, shield: Shield, "id-card": IdCard, "file-check": FileCheck, globe: Globe };

export function HomePage() {
  const { lang, t } = useLang();
  const { settings, loading } = useSettings();
  const c = settings.company ?? {};
  const hero = settings.hero ?? {};
  const stats = settings.stats ?? {};
  const whyUs = settings.why_us ?? {};
  const [services, setServices] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [rotateIdx, setRotateIdx] = useState(0);

  let waNumber = "905303385529";
  if (c.whatsapp) {
    let cleaned = c.whatsapp.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '90' + cleaned.substring(1);
    } else if (cleaned.length === 10 && cleaned.startsWith('5')) {
      cleaned = '90' + cleaned;
    }
    if (cleaned.length > 0) {
      waNumber = cleaned;
    }
  }

  let mapLink = c.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((c.name || '') + ' ' + c.address)}` : undefined;
  if (c.map_url && c.map_url.includes('pb=')) {
    const cidMatch = c.map_url.match(/!1s0x[0-9a-fA-F]+(?:%3A|:)(0x[0-9a-fA-F]+)/);
    if (cidMatch && cidMatch[1]) {
      try {
        const cid = BigInt(cidMatch[1]).toString();
        mapLink = `https://www.google.com/maps?cid=${cid}`;
      } catch (e) { }
    }
  } else if (c.map_url && !c.map_url.includes('embed')) {
    mapLink = c.map_url;
  }

  useEffect(() => {
    getServices(true)
      .then(data => setServices(data ?? []))
      .catch(err => console.error('Failed to load services:', err));

    getPublishedAnnouncements()
      .then(data => setAnnouncements((data ?? []).slice(0, 4)))
      .catch(err => console.error('Failed to load announcements:', err));
  }, []);

  useEffect(() => {
    if (services.length === 0) return;
    const id = setInterval(() => setRotateIdx(i => (i + 1) % services.length), 2500);
    return () => clearInterval(id);
  }, [services.length]);

  return (
    <div>
      <SEO description={c[`slogan_${lang}`]} />
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ height: 600, background: "linear-gradient(to right, #ffffff 0%, #136ca3 80%)" }}>
        <div className="container mx-auto h-full px-4 grid md:grid-cols-2 gap-8 items-center">
          <div className="flex items-center justify-center">
            {hero.logo_url && (
              <img
                src={hero.logo_url.startsWith('http')
                  ? hero.logo_url
                  : `${API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL}${hero.logo_url.startsWith('/') ? '' : '/'}${hero.logo_url}`}
                alt={c.name ?? "Awork"}
                className="max-h-[260px] md:max-h-[360px] w-auto object-contain animate-fade-in drop-shadow-[0_10px_25px_rgba(0,0,0,0.15)]"
              />
            )}
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">{hero[`title_${lang}`] ?? c.name}</h1>
            <p className="text-blue-50/95 mb-6 md:text-lg leading-relaxed">{hero[`subtitle_${lang}`] ?? ""}</p>
            <div className="h-20 relative overflow-hidden">
              {services.map((s, i) => (
                <div
                  key={s.id}
                  className="absolute inset-0 flex items-center justify-center md:justify-start transition-all duration-700"
                  style={{
                    opacity: i === rotateIdx ? 1 : 0,
                    transform: i === rotateIdx ? "translateY(0)" : "translateY(20px)",
                  }}
                >
                  <span className="text-xl md:text-2xl font-semibold text-primary bg-white/90 backdrop-blur px-4 py-2 shadow-sm border border-white/20">
                    {s[lang === "tr" ? "title_tr" : "title_en"]}
                  </span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-4 bg-white text-primary px-6 py-3  font-bold hover:bg-blue-50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
              {t("section.contact")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-5xl font-bold text-center text-primary mb-2">{t("section.services")}</h2>
        <div className="w-20 h-1 bg-primary mx-auto mb-12 rounded-full" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(s => {
            const Icon = iconMap[s.icon] ?? Briefcase;
            const title = lang === "tr" ? s.title_tr : s.title_en;
            const short = lang === "tr" ? s.short_tr : s.short_en;
            return (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                className="bg-card border overflow-hidden hover:shadow-xl transition-shadow group flex flex-col cursor-pointer"
              >
                <div className="h-70 bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center overflow-hidden">
                  {s.image_url ? (
                    <img src={s.image_url} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <Icon size={56} className="text-white/90" />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-primary mb-2 min-h-[3rem]">{title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-1">{short}</p>
                  <div className="inline-flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
                    {t("common.details")} <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <WhyUsSection whyUs={whyUs} stats={stats} lang={lang} t={t} />

      {/* ANNOUNCEMENTS */}
      {announcements.length > 0 && (
        <section className="container mx-auto px-4 py-16">
          <h2 className="text-5xl font-bold text-center text-primary mb-2">{t("section.announcements")}</h2>
          <div className="w-20 h-1 bg-primary mx-auto mb-12 rounded-full" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {announcements.map(a => {
              const title = lang === "tr" ? a.title_tr : a.title_en;
              const content = lang === "tr" ? a.content_tr : a.content_en;
              return (
                <Link
                  key={a.id}
                  to={`/announcements/${a.id}`}
                  className="bg-card border overflow-hidden hover:shadow-xl transition-shadow group flex flex-col cursor-pointer"
                >
                  <div className="h-70 bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center overflow-hidden">
                    {a.image_url ? (
                      <img src={a.image_url} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <Briefcase size={56} className="text-white/90" />
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-bold text-primary mb-4 flex-1">{title}</h3>
                    <div className="inline-flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
                      {t("common.details")} <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-5xl font-bold text-center text-primary mb-2">{t("section.contact")}</h2>
        <div className="w-20 h-1 bg-primary mx-auto mb-12" />
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <InfoCard icon={<Briefcase size={20} />} title={c.name ?? ""} body={c[`slogan_${lang}`]} />
            <InfoCard icon={<MapPin size={20} />} title={t("common.address")} body={c.address} href={mapLink} />
            <InfoCard icon={<Phone size={20} />} title={t("common.phone")} body={`${c.phone ?? ""}${c.phone2 ? " · " + c.phone2 : ""}`} href={c.phone ? `tel:${c.phone.replace(/\s+/g, '')}` : undefined} />
            <InfoCard
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                </svg>
              }
              title="WhatsApp"
              body={c.whatsapp || "0530 338 55 29"}
              href={`https://wa.me/${waNumber}`}
            />
            <InfoCard icon={<Mail size={20} />} title="Email" body={c.email} href={c.email ? `mailto:${c.email}` : undefined} />
            <div className="bg-card border p-5">
              <h4 className="font-bold text-primary mb-3">{t("common.followUs")}</h4>
              <div className="flex gap-3">
                {c.social?.instagram && <a href={c.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Instagram size={18} /></a>}
                {c.social?.facebook && <a href={c.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Facebook size={18} /></a>}
                {c.social?.linkedin && <a href={c.social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><Linkedin size={18} /></a>}
                {c.social?.twitter && <a href={c.social.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary-dark"><svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>}
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon, title, body, href }: any) {
  const content = (
    <>
      <div className="w-10 h-10 bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <h4 className="font-bold text-primary mb-1">{title}</h4>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </>
  );

  if (href) {
    const isExternal = href.startsWith('http');
    return (
      <a
        href={href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noreferrer" : undefined}
        className="bg-card border p-5 flex gap-4 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
      >
        {content}
      </a>
    );
  }

  return (
    <div className="bg-card border p-5 flex gap-4">
      {content}
    </div>
  );
}

function WhyUsSection({ whyUs, stats, lang, t }: any) {
  const [visible, setVisible] = useState(false);
  const [counts, setCounts] = useState({ c: 0, a: 0, k: 0 });

  useEffect(() => {
    const el = document.getElementById("why-us");
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const targets = { c: stats.customers ?? 0, a: stats.applications ?? 0, k: stats.countries ?? 0 };
    const duration = 1500;
    const start = Date.now();
    const tick = () => {
      const p = Math.min(1, (Date.now() - start) / duration);
      const ease = 1 - Math.pow(1 - p, 3);
      setCounts({
        c: Math.round(targets.c * ease),
        a: Math.round(targets.a * ease),
        k: Math.round(targets.k * ease),
      });
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, stats]);

  return (
    <section id="why-us" className="bg-accent/40 py-16">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold text-primary mb-2">{whyUs[`title_${lang}`] ?? t("section.whyUs")}</h2>
        <div className="w-20 h-1 bg-primary mx-auto mb-6 rounded-full" />
        <p className="max-w-3xl mx-auto text-muted-foreground mb-12">{whyUs[`body_${lang}`]}</p>
        <div className="grid sm:grid-cols-3 mx-auto rounded-xl divide-y sm:divide-y-0 sm:divide-x">
          <StatCard value={counts.c} suffix="+" label={t("stats.customers")} />
          <StatCard value={counts.a} suffix="+" label={t("stats.applications")} />
          <StatCard value={counts.k} suffix="" label={t("stats.countries")} />
        </div>
      </div>
    </section>
  );
}

function StatCard({ value, suffix, label }: any) {
  return (
    <div className="p-8">
      <div className="text-5xl font-bold text-primary mb-2">{value.toLocaleString()}{suffix}</div>
      <div className="text-muted-foreground font-medium">{label}</div>
    </div>
  );
}

function ContactForm() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: "" });

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: "" });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // CAPTCHA doğrulama
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      toast.error(lang === 'tr' ? 'Güvenlik kodu yanlış!' : 'Security code is incorrect!');
      generateCaptcha();
      return;
    }

    setLoading(true);
    try {
      await addContactMessage({ ...form, captcha: captcha.answer, captchaSum: correctAnswer });
      toast.success(t("form.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      generateCaptcha();
    } catch (error) {
      toast.error(t("form.error"));
      generateCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-card border p-6 space-y-4 flex flex-col h-full">
      <Field label={t("common.name")} value={form.name} onChange={(v: string) => setForm({ ...form, name: v })} required />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("common.email")} value={form.email} type="email" onChange={(v: string) => setForm({ ...form, email: v })} required />
        <Field label={t("common.phone")} value={form.phone} onChange={(v: string) => setForm({ ...form, phone: v })} />
      </div>
      <Field label={t("common.subject")} value={form.subject} onChange={(v: string) => setForm({ ...form, subject: v })} />
      <div className="flex-1 flex flex-col">
        <label className="block text-sm font-medium mb-1">{t("common.message")}</label>
        <textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full flex-1 border px-3 py-2 bg-background min-h-[120px]" />
      </div>
      <div className="bg-primary/5 border-2 border-primary/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="text-primary" size={20} />
          <label className="block text-sm font-bold text-primary">
            {lang === 'tr' ? 'Güvenlik Kodu' : 'Security Code'} *
          </label>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border-2 border-primary  px-4 py-2 font-mono text-xl font-bold text-primary">
            {captcha.num1} + {captcha.num2} = ?
          </div>
          <input
            required
            type="number"
            value={captcha.answer}
            onChange={e => setCaptcha({ ...captcha, answer: e.target.value })}
            placeholder={lang === 'tr' ? 'Cevap' : 'Answer'}
            className="w-24 border-2 border-primary px-3 py-2 bg-white text-center font-bold text-lg"
          />
        </div>
      </div>
      <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3 font-medium hover:bg-primary-dark disabled:opacity-50">{loading ? "..." : t("common.send")}</button>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", required }: any) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}{required && " *"}</label>
      <input required={required} type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full border px-3 py-2 bg-background" />
    </div>
  );
}
